import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { US_STATES } from "@/lib/us-states";
import { isValidNPI } from "@/lib/npi-validation";

interface CredentialInitialValues {
  npiNumber?: string;
  licenseNumber?: string;
  licenseState?: string;
  licenseExpiry?: string;
  deaNumber?: string;
  deaExpiry?: string;
  specialty?: string;
  bio?: string;
  videoRoomUrl?: string;
}

const VIDEO_ROOM_URL_PATTERN = /^https:\/\/(doxy\.me|[a-z0-9-]+\.doxy\.me|[a-z0-9-]+\.daily\.co|zoom\.us|[a-z0-9-]+\.zoom\.us)\/.+/i;

interface CredentialSubmissionFormProps {
  userId: string;
  onSubmitted: () => void;
  /** When provided, form is in resubmission mode (update instead of insert) */
  initialValues?: CredentialInitialValues;
  isResubmission?: boolean;
}

const formatDateInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const displayToIso = (display: string): string => {
  const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";
  return `${match[3]}-${match[1]}-${match[2]}`;
};

const isoToDisplay = (iso: string): string => {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return iso;
  return `${match[2]}/${match[3]}/${match[1]}`;
};

const CredentialSubmissionForm = ({ userId, onSubmitted, initialValues, isResubmission }: CredentialSubmissionFormProps) => {
  const { toast } = useToast();
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [npiNumber, setNpiNumber] = useState(initialValues?.npiNumber || "");
  const [licenseNumber, setLicenseNumber] = useState(initialValues?.licenseNumber || "");
  const [licenseState, setLicenseState] = useState(initialValues?.licenseState || "");
  const [licenseExpiry, setLicenseExpiry] = useState(initialValues?.licenseExpiry || "");
  const [deaNumber, setDeaNumber] = useState(initialValues?.deaNumber || "");
  const [deaExpiry, setDeaExpiry] = useState(initialValues?.deaExpiry || "");
  const [specialty, setSpecialty] = useState(initialValues?.specialty || "");
  const [bio, setBio] = useState(initialValues?.bio || "");
  const [videoRoomUrl, setVideoRoomUrl] = useState(initialValues?.videoRoomUrl || "");

  const handleDateField = (setter: (v: string) => void, rawValue: string) => {
    const display = formatDateInput(rawValue);
    const iso = displayToIso(display);
    setter(iso || display);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!npiNumber || !isValidNPI(npiNumber)) {
      errs.npiNumber = "Valid 10-digit NPI number required";
    }
    if (!licenseNumber.trim()) {
      errs.licenseNumber = "Medical license number required";
    }
    if (!licenseState) {
      errs.licenseState = "License state required";
    }
    if (!licenseExpiry) {
      errs.licenseExpiry = "License expiration date required";
    } else if (new Date(licenseExpiry) <= new Date()) {
      errs.licenseExpiry = "License must not be expired";
    }
    if (deaNumber && !deaExpiry) {
      errs.deaExpiry = "DEA expiration required when DEA number is provided";
    }
    if (deaExpiry && new Date(deaExpiry) <= new Date()) {
      errs.deaExpiry = "DEA must not be expired";
    }
    if (videoRoomUrl.trim() && !VIDEO_ROOM_URL_PATTERN.test(videoRoomUrl.trim())) {
      errs.videoRoomUrl = "Must be a HIPAA-compliant Doxy.me, Daily.co, or Zoom for Healthcare URL";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const credentialData = {
        npi_number: npiNumber,
        medical_license_number: licenseNumber.trim(),
        license_state: licenseState,
        license_expiry: licenseExpiry,
        dea_number: deaNumber.trim() || null,
        dea_expiry: deaExpiry || null,
        specialty: specialty.trim() || null,
        bio: bio.trim() || null,
        video_room_url: videoRoomUrl.trim() || null,
      } as any;

      let error;
      if (isResubmission) {
        // Update existing rejected credentials and reset to pending
        const result = await supabase
          .from("provider_credentials")
          .update({ ...credentialData, credential_status: "pending_review", rejection_reason: null })
          .eq("user_id", userId);
        error = result.error;
      } else {
        const result = await supabase
          .from("provider_credentials")
          .insert({ user_id: userId, ...credentialData });
        error = result.error;
      }

      if (error) throw error;

      // Notify admins on resubmission
      if (isResubmission) {
        try {
          const { data: adminRoles } = await supabase
            .from("user_roles")
            .select("user_id")
            .eq("role", "admin");

          if (adminRoles && adminRoles.length > 0) {
            const notifications = adminRoles.map((r) => ({
              user_id: r.user_id,
              title: "Provider Credentials Resubmitted",
              message: `A provider has updated and resubmitted their credentials for review.`,
              type: "provider_resubmission",
              related_id: userId,
            }));
            await supabase.from("notifications").insert(notifications);
          }
        } catch (notifErr) {
          console.error("Failed to notify admins:", notifErr);
        }
      }

      setSubmitted(true);
      toast({
        title: "Credentials submitted",
        description: "Your credentials are now pending review.",
      });
      onSubmitted();
    } catch (err: any) {
      const code = err?.code || "";
      const msg = (err?.message || "").toLowerCase();
      const isRlsError =
        code === "42501" ||
        code === "PGRST301" ||
        msg.includes("row-level security") ||
        msg.includes("row level security") ||
        msg.includes("violates row-level") ||
        msg.includes("permission denied");

      if (isRlsError) {
        // Note: admin_error_log entry is created automatically by the global
        // RLS interceptor in src/integrations/supabase/rls-interceptor.ts —
        // no manual logError call needed here.
        toast({
          title: "We couldn't save your credentials",
          description:
            "Your account doesn't have permission to submit credentials yet. Please contact support@elarahealth.life and we'll get you set up right away.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Submission failed",
          description: err?.message || "Something went wrong. Please try again or contact support@elarahealth.life.",
          variant: "destructive",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-6 space-y-3">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <CheckCircle2 size={24} className="text-primary" />
        </div>
        <p className="text-sm text-muted-foreground">
          Your credentials have been submitted and are pending review.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {isResubmission
          ? "Update your credentials below and resubmit for review."
          : "Submit your credentials to start seeing patients. All fields except DEA are required."}
      </p>

      {/* NPI */}
      <div className="space-y-2">
        <Label htmlFor="cred-npi">NPI Number</Label>
        <Input
          id="cred-npi"
          placeholder="10-digit NPI"
          maxLength={10}
          value={npiNumber}
          onChange={(e) => {
            setNpiNumber(e.target.value.replace(/\D/g, ""));
            setErrors((prev) => ({ ...prev, npiNumber: "" }));
          }}
        />
        {errors.npiNumber && <p className="text-sm text-destructive">{errors.npiNumber}</p>}
      </div>

      {/* License */}
      <div className="space-y-2">
        <Label htmlFor="cred-license">Medical License Number</Label>
        <Input
          id="cred-license"
          value={licenseNumber}
          onChange={(e) => {
            setLicenseNumber(e.target.value);
            setErrors((prev) => ({ ...prev, licenseNumber: "" }));
          }}
        />
        {errors.licenseNumber && <p className="text-sm text-destructive">{errors.licenseNumber}</p>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>License State</Label>
          <Select value={licenseState} onValueChange={(v) => { setLicenseState(v); setErrors((prev) => ({ ...prev, licenseState: "" })); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.licenseState && <p className="text-sm text-destructive">{errors.licenseState}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="cred-license-expiry">License Expiry</Label>
          <Input
            id="cred-license-expiry"
            placeholder="MM/DD/YYYY"
            value={isoToDisplay(licenseExpiry) || licenseExpiry}
            onChange={(e) => {
              handleDateField(setLicenseExpiry, e.target.value);
              setErrors((prev) => ({ ...prev, licenseExpiry: "" }));
            }}
            maxLength={10}
          />
          {errors.licenseExpiry && <p className="text-sm text-destructive">{errors.licenseExpiry}</p>}
        </div>
      </div>

      {/* Specialty & Bio */}
      <div className="pt-2 border-t border-border space-y-4">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-1">
          Professional Profile <span className="font-normal normal-case">(optional)</span>
        </p>
        <div className="space-y-2">
          <Label htmlFor="cred-specialty">Specialty</Label>
          <Input
            id="cred-specialty"
            placeholder="e.g. Internal Medicine, Endocrinology"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cred-bio">Bio</Label>
          <textarea
            id="cred-bio"
            placeholder="Brief professional bio visible to patients"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={3}
            maxLength={500}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <p className="text-xs text-muted-foreground text-right">{bio.length}/500</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="cred-video-url">HIPAA-compliant video room URL</Label>
          <Input
            id="cred-video-url"
            placeholder="https://doxy.me/dr-smith"
            value={videoRoomUrl}
            onChange={(e) => {
              setVideoRoomUrl(e.target.value);
              setErrors((prev) => ({ ...prev, videoRoomUrl: "" }));
            }}
          />
          <p className="text-xs text-muted-foreground">
            Optional. Default consults are async via the secure inbox. Paste a Doxy.me, Daily.co, or Zoom for Healthcare room URL if you want to offer live video. The vendor must sign a BAA.
          </p>
          {errors.videoRoomUrl && <p className="text-sm text-destructive">{errors.videoRoomUrl}</p>}
        </div>
      </div>

      {/* DEA (optional) */}
      <div className="pt-2 border-t border-border space-y-4">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-1">
          DEA Registration <span className="font-normal normal-case">(optional)</span>
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cred-dea">DEA Number</Label>
            <Input
              id="cred-dea"
              value={deaNumber}
              onChange={(e) => setDeaNumber(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cred-dea-expiry">DEA Expiry</Label>
            <Input
              id="cred-dea-expiry"
              placeholder="MM/DD/YYYY"
              value={isoToDisplay(deaExpiry) || deaExpiry}
              onChange={(e) => {
                handleDateField(setDeaExpiry, e.target.value);
                setErrors((prev) => ({ ...prev, deaExpiry: "" }));
              }}
              maxLength={10}
            />
            {errors.deaExpiry && <p className="text-sm text-destructive">{errors.deaExpiry}</p>}
          </div>
        </div>
      </div>

      <Button type="submit" disabled={submitting} className="w-full gap-2">
        {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
        {submitting ? "Submitting..." : isResubmission ? "Resubmit Credentials" : "Submit Credentials"}
      </Button>
    </form>
  );
};

export default CredentialSubmissionForm;
